import React, { useEffect, useState, useMemo, useRef } from 'react';
import Styled from 'styled';
import media from 'lib/media/media';
import defaultStyles from './masonry.scss';
import MasonryImage from './masonry-image/masonry-image';
import FeedImages, { OnClick } from 'four-nets/photoblog/posts/common/feed-images/feed-images';
import { MasonryFragment } from './masonry.graphql';

export type MediaKey = 'ms' | 'sm' | 'md' | 'lg' | 'xlg' | 'xxlg';

type ImageNodeEdges = NonNullable<MasonryFragment['edges']>;
type ImageNode = NonNullable<NonNullable<NonNullable<MasonryFragment['edges']>[0]>['node']>;

interface Props extends Partial<MasonryFragment> {
    styles: typeof defaultStyles;
    columnsSizes?: Map<MediaKey, number>;
    fetchMoreFn: any;
    disableLazyLoad?: boolean;
}
const DEFAULT_MASONRY_COLUMN_COUNT = 4;

const Masonry: React.FC<Props> = React.memo(props => {
    const columnSizesProperties = useRef<{ mediaKey: MediaKey; columnCount: number; mql: MediaQueryList }[]>([]);
    const [currentColumnsCount, setCurrentColumnsCount] = useState<number>(DEFAULT_MASONRY_COLUMN_COUNT);

    useEffect(() => {
        const handleResize = () => {
            const matched = [...columnSizesProperties.current].reverse().find(triple => triple.mql.matches);

            if (matched) {
                setCurrentColumnsCount(matched.columnCount);
            } else if (columnSizesProperties.current.length) {
                setCurrentColumnsCount(columnSizesProperties.current[0].columnCount);
            }
        };

        const properties: { mediaKey: MediaKey; columnCount: number; mql: MediaQueryList }[] = [];

        if (props.columnsSizes) {
            props.columnsSizes.forEach((columnCount, mediaKey) => {
                properties.push({ mediaKey, columnCount, mql: window.matchMedia(media[mediaKey]) });
            });
            properties.forEach(triple => triple.mql.addListener(handleResize));

            columnSizesProperties.current = properties;
            handleResize();
        }

        return () => {
            columnSizesProperties.current.forEach(triple => triple.mql.removeListener(handleResize));
        };
    }, [props.columnsSizes]);

    const splitIntoColumns = (array: ImageNodeEdges | null, columnCount: number = DEFAULT_MASONRY_COLUMN_COUNT) => {
        let result: { node: ImageNode; index: number }[][] = [];

        if (!array) {
            return [];
        }

        result = new Array(columnCount);
        const heights = new Array(columnCount);

        for (let i: number = 0; i < columnCount; i++) {
            result[i] = [];
            heights[i] = 0;
        }

        array.forEach((edge, index) => {
            const minHeight = Math.min(...heights);
            const minIndex = heights.indexOf(minHeight);

            const penaltyForElementExtraPaddingHeight = 30;
            const relativeHeight = Math.round((edge?.node?.photoFile?.height! / edge?.node?.photoFile?.width!) * 100);
            heights[minIndex] += relativeHeight + penaltyForElementExtraPaddingHeight;

            result[minIndex].push({ node: edge!.node!, index });
        });

        return result;
    };

    const { styles, edges, fetchMoreFn, disableLazyLoad, ...rest } = props;

    const columns = useMemo(() => splitIntoColumns(edges!, currentColumnsCount), [currentColumnsCount, edges]);

    return (
        <FeedImages fetchMore={fetchMoreFn} fetchMoreIndex={20} {...({ ...rest, images: { edges } } as any)}>
            {(
                imgRef: (element: HTMLImageElement | null, { id }: { id: number | string }) => void,
                onClick: OnClick
            ) => {
                return (
                    <div className={styles.imagesGrid}>
                        {columns
                            ? columns.map((column, columnIndex: number) => {
                                  return column ? (
                                      <div key={`${columnIndex}${JSON.stringify(column)}`} className={styles.column}>
                                          {column.map(({ node, index }) => {
                                              return (
                                                  <MasonryImage
                                                      key={node.id}
                                                      index={index}
                                                      onClick={onClick as any}
                                                      imgRef={imgRef}
                                                      disableLazyLoad={disableLazyLoad}
                                                      {...node}
                                                  />
                                              );
                                          })}
                                      </div>
                                  ) : null;
                              })
                            : null}
                    </div>
                );
            }}
        </FeedImages>
    );
});

export default Styled(defaultStyles)(Masonry);
